import { motion } from "framer-motion";
import { Flame, Star, Clock, MapPin, Phone, ExternalLink, ShoppingBag } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { MenuItem, LocationInfo, formatPrice } from "@/lib/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

interface MenuCardProps {
  item: MenuItem;
}

export function MenuCard({ item }: MenuCardProps) {
  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="h-full"
    >
      <Card className="overflow-hidden border-border/40 bg-card/50 backdrop-blur-sm h-full flex flex-col group">
        <div className="relative aspect-square overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {item.isBestSeller && (
              <Badge className="bg-primary text-primary-foreground border-none shadow-lg">
                <Star className="w-3 h-3 mr-1 fill-current" /> Best Seller
              </Badge>
            )}
            {item.isSpicy && (
              <Badge className="bg-destructive text-destructive-foreground border-none shadow-lg">
                <Flame className="w-3 h-3 mr-1 fill-current" /> Spicy
              </Badge>
            )}
          </div>
        </div>
        
        <CardHeader className="p-4 pb-2">
          <div className="flex justify-between items-start gap-2">
            <CardTitle className="text-lg font-bold leading-tight">{item.name}</CardTitle>
            <span className="font-mono font-bold text-primary text-sm whitespace-nowrap">
              {formatPrice(item.price)}
            </span>
          </div>
          <Badge variant="secondary" className="w-fit text-[10px] uppercase tracking-wider">
            {item.category}
          </Badge>
        </CardHeader>

        <CardContent className="p-4 pt-0 flex-grow">
          <p className="text-sm text-muted-foreground line-clamp-2">
            {item.description}
          </p>
          {item.portionSize && (
            <p className="text-[11px] mt-2 text-primary/70 font-medium">
              Portion: {item.portionSize}
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

export function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-card border border-border/50 shadow-sm hover:shadow-md transition-all"
    >
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </motion.div>
  );
}

interface LocationCardProps {
  location: LocationInfo;
}

export function LocationCard({ location }: LocationCardProps) {
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-card to-accent/20 overflow-hidden">
      <CardHeader className="bg-primary text-primary-foreground p-6">
        <CardTitle className="flex items-center gap-2 text-2xl">
          <MapPin className="w-6 h-6" />
          {location.mallName}
        </CardTitle>
        <p className="opacity-90 font-medium">{location.unitNumber}</p>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="mt-1 text-primary">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold mb-1">Operating Hours</p>
              <div className="text-sm text-muted-foreground font-mono space-y-1">
                <p>Weekdays: {location.operatingHours.weekdays}</p>
                <p>Weekends: {location.operatingHours.weekends}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="mt-1 text-primary">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold mb-1">Contact Us</p>
              <p className="text-sm text-muted-foreground">{location.phone}</p>
            </div>
          </div>

          <div className="pt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground italic">
              Landmark: {location.landmark}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button 
            asChild
            variant="outline" 
            className="w-full border-primary/30 text-primary hover:bg-primary/5"
          >
            <a 
              href={location.googleMapsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Directions
            </a>
          </Button>
          <Button 
            asChild
            variant="outline" 
            className="w-full border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/5"
          >
            <a 
              href={`https://wa.me/${location.whatsapp}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              <SiWhatsapp className="w-4 h-4" /> WhatsApp
            </a>
          </Button>
        </div>
      </CardContent>

      <CardFooter className="p-6 pt-0">
        <Button 
          asChild
          size="lg" 
          className="w-full bg-[#00B14F] hover:bg-[#009642] text-white border-none shadow-lg group"
        >
          <a 
            href={location.grabLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-5 h-5 transition-transform group-hover:scale-110" />
            Order via GrabFood
            <ExternalLink className="w-4 h-4 ml-1 opacity-70" />
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
}
